import { useIsReadOnly } from '@/lib/casl'
import { cn } from '@/lib/utils'
import { useId } from 'react'
import { Package, Tags } from 'lucide-react'
import { Checkbox } from '../ui/checkbox'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { SegmentedControl, SegmentedControlItem } from '../ui/segmented-control'
import { Typography } from '@/components/ui/typography'
const SCOPE_GLOBAL = 'global'
const SCOPE_COLLECTIONS = 'collections'
const treatedUnselectedScopeTone =
  'border-border/70 bg-background text-foreground hover:border-border hover:bg-muted/45 hover:text-foreground dark:hover:border-white/20 dark:hover:bg-white/[0.06]'

const scopeOptions = [
  {
    id: SCOPE_GLOBAL,
    name: 'All Products',
    icon: Package,
    iconTone: 'text-chart-5',
  },
  {
    id: SCOPE_COLLECTIONS,
    name: 'Selected Collection(s)',
    icon: Tags,
    iconTone: 'text-chart-1',
  },
]

const treatedScopeButtonClass =
  'h-auto min-w-0 justify-start gap-3 rounded-xl border px-4 py-3 text-left whitespace-normal shadow-none transition-colors'
const treatedScopeIconClass = 'mt-0.5 shrink-0 rounded-xl border border-current/15 bg-card p-2'
const treatedScopeLabelClass = 'min-w-0 text-base font-semibold leading-tight whitespace-normal'

const normalizeCollectionIds = (value) =>
  Array.isArray(value)
    ? Array.from(new Set(value.map((id) => String(id)).filter((id) => id !== '')))
    : []

const sortIds = (ids) => [...ids].sort((left, right) => Number(left) - Number(right))

const ProductPriceAdjustmentScopePicker = ({
  collectionIds = [],
  collections = [],
  onCollectionIdsChange,
  onScopeTypeChange,
  showPageTreatment = false,
  scopeType = '',
}) => {
  const { isReadOnly } = useIsReadOnly()
  const id = useId()
  const scopeTypeLabelId = `${id}-scope-type-label`
  const selectedCollectionIds = normalizeCollectionIds(collectionIds)
  const showCollections = scopeType === SCOPE_COLLECTIONS
  const ScopeGroup = showPageTreatment ? 'div' : SegmentedControl
  const ScopeButton = showPageTreatment ? Button : SegmentedControlItem
  const handleScopeTypeChange = (nextScopeType) => {
    onScopeTypeChange?.(nextScopeType)

    if (nextScopeType === SCOPE_GLOBAL) {
      onCollectionIdsChange?.([])
    }
  }

  return (
    <div className={cn('grid gap-4', showPageTreatment && '@container/price-adjustment-scope')}>
      <div className="grid gap-2">
        <Label id={scopeTypeLabelId}>What products should this change apply to?</Label>
        <ScopeGroup
          role="radiogroup"
          aria-labelledby={scopeTypeLabelId}
          aria-orientation="horizontal"
          className={
            showPageTreatment
              ? 'grid gap-3 @[34rem]/price-adjustment-scope:grid-cols-2'
              : 'grid w-full grid-cols-2 sm:inline-flex sm:w-auto'
          }
        >
          {scopeOptions.map((option) => {
            const optionId = String(option.id)
            const isSelected = scopeType === optionId
            const Icon = option.icon

            return (
              <ScopeButton
                key={option.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={option.name}
                id={`${id}-scope-type-${option.id}`}
                variant={showPageTreatment ? (isSelected ? 'selection' : 'outline') : 'outline'}
                size="sm"
                selected={showPageTreatment ? undefined : isSelected}
                disabled={isReadOnly}
                className={cn(
                  showPageTreatment ? treatedScopeButtonClass : 'min-w-0 flex-1 px-4 sm:flex-none',
                  showPageTreatment && !isSelected && treatedUnselectedScopeTone
                )}
                onClick={() => handleScopeTypeChange(optionId)}
              >
                {showPageTreatment ? (
                  <span className={cn(treatedScopeIconClass, option.iconTone)} aria-hidden="true">
                    <Icon className="size-5" />
                  </span>
                ) : null}
                {showPageTreatment ? (
                  <span className={treatedScopeLabelClass}>{option.name}</span>
                ) : (
                  option.name
                )}
              </ScopeButton>
            )
          })}
        </ScopeGroup>
      </div>

      {showCollections ? (
        <div className="space-y-3">
          <Label>Collections</Label>

          {collections.length > 0 ? (
            <div className="space-y-3">
              {collections.map((collection) => {
                const collectionId = String(collection.id)
                const checkboxId = `${id}-collection-${collectionId}`

                return (
                  <div key={collectionId} className="flex items-start gap-3">
                    <Checkbox
                      id={checkboxId}
                      type="button"
                      checked={selectedCollectionIds.includes(collectionId)}
                      disabled={isReadOnly}
                      onCheckedChange={(checked) => {
                        const nextCollectionIds = checked
                          ? sortIds([...selectedCollectionIds, collectionId])
                          : selectedCollectionIds.filter((id) => id !== collectionId)

                        onCollectionIdsChange?.(nextCollectionIds)
                      }}
                    />
                    <Label
                      htmlFor={checkboxId}
                      appearance="choice"
                      weight="normal"
                      className="grow"
                    >
                      {collection.name}
                    </Label>
                  </div>
                )
              })}
            </div>
          ) : (
            <Typography variant="supporting">No collections yet.</Typography>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default ProductPriceAdjustmentScopePicker
